/**
 * Rewrite tab — paste a URL, the engine resolves the Sanity doc, runs ONE
 * Claude call to rewrite preserving JSON shape (with the Notion SEO keyword as
 * a hard constraint), shows a side-by-side diff, lets you edit per-section or
 * paste a corrected version, then push as a Sanity draft.
 *
 * The 11 existing per-type generators are NOT used by this tab. The
 * /content/generate path stays exactly as it was.
 */

import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import {
  usePreviewRewrite,
  useSaveProposedEnvelope,
  usePushRewrite,
  useContentVersions,
  useRewriteNotionSources,
  useRewriteProject,
  usePatchContentItem,
  useApproveContentItem,
  useRewriteModifications,
  usePollRewriteNow,
  useReExtractModification,
} from '../../hooks/useContent';
import { useAuth } from '../../hooks/useAuth';
import type {
  RewritePreviewResponse,
  RewriteDiff,
  ModificationListItem,
} from '../../api/content';
import { REVIEWERS, type ReviewMeta } from './reviewers';
import { formatDayMonthYearTime, formatDayMonthYear } from '../../utils/dateFormat';
type EditorSource = 'manual_edit' | 'paste_replacement' | 'section_edit';
type ItemStatus = 'draft' | 'review' | 'approved' | 'published';

interface ProjectState {
  contentItemId: string;
  sanityDocumentId: string;
  documentType: string;
  contentType: string | null;
  slug: string | null;
  language: string;
  title: string;
  url: string;
  existing: Record<string, unknown>;
  proposed: Record<string, unknown>;
  diff: RewriteDiff;
  seoKeyword: string | null;
  keywordWarnings: string[];
  reusedProject: boolean;
  isBodyBased: boolean;
  itemStatus: ItemStatus;
  reviewMeta: ReviewMeta | null;
  previewIframeUrl: string | null;
}

const BODY_BASED_TYPES = new Set(['article', 'glossaryTerm']);

interface RewriteTabProps {
  /** When set, loads the stored rewrite project on mount (used when opening
   *  a project from the Library tab). */
  initialProjectId?: string | null;
  /** Called once the initial project has been hydrated, so the parent can
   *  clear its "open this project" intent. */
  onInitialProjectLoaded?: () => void;
  /** Optional shared Notion source selection supplied by the Website tab. */
  sharedNotionDatabaseIds?: string[];
  /** When true, hides the built-in checkbox selector and reuses the shared selection. */
  useSharedNotionSources?: boolean;
}

export function RewriteTab({
  initialProjectId = null,
  onInitialProjectLoaded,
  sharedNotionDatabaseIds,
  useSharedNotionSources = false,
}: RewriteTabProps = {}) {
  const [url, setUrl] = useState('');
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<string>>(new Set());
  const [sourceDefaultsApplied, setSourceDefaultsApplied] = useState(false);
  const [customSourcesText, setCustomSourcesText] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [project, setProject] = useState<ProjectState | null>(null);
  const [questionsState, setQuestionsState] = useState<{
    questions: { field: string; prompt: string; suggestion: string | null; source: string }[];
    sanityDocumentId: string;
    documentType: string;
    contentType: string | null;
  } | null>(null);

  const previewMutation = usePreviewRewrite();
  const saveMutation = useSaveProposedEnvelope();
  const pushMutation = usePushRewrite();
  const sourcesQuery = useRewriteNotionSources();
  const initialProject = useRewriteProject(initialProjectId);

  // Apply server-provided defaults once when the source list arrives.
  useEffect(() => {
    if (!sourcesQuery.data || sourceDefaultsApplied) return;
    const defaults = new Set(sourcesQuery.data.sources.filter((s) => s.defaultOn).map((s) => s.id));
    setSelectedSourceIds(defaults);
    setSourceDefaultsApplied(true);
  }, [sourceDefaultsApplied, sourcesQuery.data]);

  // When the parent asks us to open an existing project (Library → Rewrite),
  // hydrate state from the fetched project once.
  const initialProjectData = initialProject.data;
  useEffect(() => {
    if (!initialProjectData) return;
    applyPreview(initialProjectData);
    setUrl(initialProjectData.url);
    onInitialProjectLoaded?.();
  }, [initialProjectData, onInitialProjectLoaded]);

  function toggleSource(id: string) {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  /** Combine selected registered ids with any custom URLs/IDs the user pasted. */
  function buildNotionDatabaseIds(): string[] {
    const customs = customSourcesText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const baseIds = useSharedNotionSources
      ? (sharedNotionDatabaseIds ?? [])
      : [...selectedSourceIds];
    return [...baseIds, ...customs];
  }

  function applyPreview(response: RewritePreviewResponse) {
    if (response.status === 'questions') {
      setQuestionsState({
        questions: response.questions,
        sanityDocumentId: response.sanityDocumentId,
        documentType: response.documentType,
        contentType: response.contentType,
      });
      // Pre-fill answers from any Product Database suggestions so the user sees
      // them as a confirmation step rather than a blank textarea. Only fills
      // fields the user hasn't already typed into.
      setAnswers((prev) => {
        const next = { ...prev };
        for (const q of response.questions) {
          if (q.suggestion && !(next[q.field]?.trim())) {
            next[q.field] = q.suggestion;
          }
        }
        return next;
      });
      setProject(null);
      return;
    }
    setQuestionsState(null);
    setProject({
      contentItemId: response.contentItemId,
      sanityDocumentId: response.sanityDocumentId,
      documentType: response.documentType,
      contentType: response.contentType,
      slug: response.slug,
      language: response.language,
      title: response.title,
      url: response.url,
      existing: response.existing,
      proposed: response.proposed,
      diff: response.diff,
      seoKeyword: response.seoKeyword,
      keywordWarnings: response.keywordWarnings,
      reusedProject: response.reusedProject,
      isBodyBased: BODY_BASED_TYPES.has(response.documentType),
      itemStatus: response.itemStatus,
      reviewMeta: response.reviewMeta as ReviewMeta | null,
      previewIframeUrl: response.previewIframeUrl,
    });
  }

  async function handleLoad() {
    if (!url.trim()) return;
    pushMutation.reset();
    saveMutation.reset();
    try {
      const result = await previewMutation.mutateAsync({
        url: url.trim(),
        notionDatabaseIds: buildNotionDatabaseIds(),
      });
      applyPreview(result);
    } catch {
      // Captured by previewMutation.isError below.
    }
  }

  async function handleSubmitAnswers() {
    if (!url.trim()) return;
    try {
      const result = await previewMutation.mutateAsync({
        url: url.trim(),
        notionDatabaseIds: buildNotionDatabaseIds(),
        answers,
      });
      applyPreview(result);
    } catch {
      // Captured by previewMutation.isError below.
    }
  }

  async function handleSaveEdit(envelope: Record<string, unknown>, source: EditorSource) {
    if (!project) return;
    pushMutation.reset();
    const result = await saveMutation.mutateAsync({
      contentItemId: project.contentItemId,
      envelope,
      source,
    });
    setProject({
      ...project,
      proposed: envelope,
      keywordWarnings: result.keywordWarnings,
    });
  }

  async function handlePush(force = false) {
    if (!project) return;
    await pushMutation.mutateAsync({ contentItemId: project.contentItemId, force });
  }

  async function handleRerun() {
    if (!project) return;
    saveMutation.reset();
    pushMutation.reset();
    try {
      const result = await previewMutation.mutateAsync({
        url: project.url,
        notionDatabaseIds: buildNotionDatabaseIds(),
      });
      applyPreview(result);
    } catch {
      // Captured by previewMutation.isError below.
    }
  }

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-4">
        <h3 className="text-body-md font-medium text-text-primary">Rewrite a page from its URL</h3>
        <p className="text-body-xs text-text-tertiary">
          Paste a public URL (e.g. <code>https://sequesto.com/integrations/odoo</code> or the staging URL <code>https://d366rkx5tq6yc.cloudfront.net/solutions/questionnaire-automation</code>) or a Sanity Studio URL. The OS resolves the document by path, so any host fronting the same Sanity dataset works. The OS will fetch the existing page, pull the Notion brief, and rewrite the copy preserving the JSON shape. SEO keywords from Notion are treated as a hard constraint.
        </p>
        <div>
          <label className="block text-body-xs font-medium text-text-secondary mb-1">URL</label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://sequesto.com/integrations/odoo"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-body-xs font-medium text-text-secondary">Brief sources from Notion</label>
          {useSharedNotionSources ? (
            <div className="rounded-md border border-border bg-surface-subtle p-3 text-body-xs text-text-secondary">
              Using the shared Website tab prompt-source selection
              {sharedNotionDatabaseIds && sharedNotionDatabaseIds.length > 0
                ? ` (${sharedNotionDatabaseIds.length} selected).`
                : '.'}
            </div>
          ) : (
            <>
              {sourcesQuery.isLoading && <Spinner />}
              {sourcesQuery.data?.sources.map((s) => {
                const checked = selectedSourceIds.has(s.id);
                return (
                  <label key={s.id} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSource(s.id)}
                      className="mt-1 h-4 w-4 cursor-pointer rounded-sm border-border accent-text-primary"
                    />
                    <div className="flex-1">
                      <div className="text-body-sm text-text-primary">
                        {s.label}
                        {s.role === 'primary' && (
                          <span className="ml-2"><Badge variant="info">primary</Badge></span>
                        )}
                      </div>
                      <div className="text-body-xs text-text-tertiary">{s.description}</div>
                    </div>
                  </label>
                );
              })}
            </>
          )}
          <div className="pt-1">
            <label className="block text-body-xs font-medium text-text-secondary mb-1">
              + Add custom Notion DB URLs / IDs (one per line)
            </label>
            <textarea
              value={customSourcesText}
              onChange={(e) => setCustomSourcesText(e.target.value)}
              rows={2}
              placeholder="https://www.notion.so/sequesto/335a5798…   or just  335a5798390e80f59c87dcaebe644c8e"
              className="w-full rounded-md border border-border bg-surface-primary px-3 py-2 text-body-xs text-text-primary font-mono"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleLoad} disabled={!url.trim() || previewMutation.isPending}>
            {previewMutation.isPending ? 'Working… ~30s' : 'Load and rewrite'}
          </Button>
          {project && (
            <Button variant="outline" onClick={handleRerun} disabled={previewMutation.isPending}>
              Re-run rewrite
            </Button>
          )}
        </div>
        {previewMutation.isError && (
          <Alert variant="error">
            {previewMutation.error instanceof Error ? previewMutation.error.message : 'Rewrite failed'}
          </Alert>
        )}
      </Card>

      {questionsState && (
        <QuestionsCard
          questions={questionsState.questions}
          answers={answers}
          onChange={setAnswers}
          onSubmit={handleSubmitAnswers}
          submitting={previewMutation.isPending}
        />
      )}

      {previewMutation.isPending && !project && !questionsState && (
        <Card className="p-6 flex items-center gap-3">
          <Spinner />
          <span className="text-body-sm text-text-secondary">
            Resolving page → loading brief → rewriting with Sonnet 4.6 → diffing. This takes ~15–30s.
          </span>
        </Card>
      )}

      {project && (
        <ProjectView
          project={project}
          onSave={handleSaveEdit}
          onPush={handlePush}
          saving={saveMutation.isPending}
          pushing={pushMutation.isPending}
          pushResult={pushMutation.data ?? null}
          pushError={pushMutation.isError ? pushMutation.error : null}
        />
      )}
    </div>
  );
}

interface QuestionsCardProps {
  questions: { field: string; prompt: string; suggestion: string | null; source: string }[];
  answers: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  onSubmit: () => void | Promise<void>;
  submitting: boolean;
}

function QuestionsCard({ questions, answers, onChange, onSubmit, submitting }: QuestionsCardProps) {
  return (
    <Card className="p-6 space-y-3">
      <h3 className="text-body-md font-medium text-text-primary">Missing brief fields</h3>
      <p className="text-body-xs text-text-tertiary">
        The Notion brief for this page is missing some required fields. Where possible, values have been pre-filled from the Product Database — review, edit if needed, and confirm.
      </p>
      {questions.map((q) => (
        <div key={q.field}>
          <label className="block text-body-xs font-medium text-text-secondary mb-1">{q.field}</label>
          <p className="text-body-xs text-text-tertiary mb-1 italic">{q.source}</p>
          <textarea
            value={answers[q.field] ?? ''}
            onChange={(e) => onChange({ ...answers, [q.field]: e.target.value })}
            rows={4}
            placeholder={q.prompt}
            className="w-full rounded-md border border-border bg-surface-primary px-3 py-2 text-body-sm text-text-primary"
          />
        </div>
      ))}
      <Button
        onClick={onSubmit}
        disabled={submitting || questions.some((q) => !(answers[q.field] ?? '').trim())}
      >
        {submitting ? 'Working…' : 'Generate rewrite with these answers'}
      </Button>
    </Card>
  );
}

interface ProjectViewProps {
  project: ProjectState;
  onSave: (envelope: Record<string, unknown>, source: EditorSource) => void | Promise<void>;
  onPush: (force?: boolean) => void | Promise<void>;
  saving: boolean;
  pushing: boolean;
  pushResult: NonNullable<ReturnType<typeof usePushRewrite>['data']> | null;
  pushError: unknown;
}

function ProjectView({ project, onSave, onPush, saving, pushing, pushResult, pushError }: ProjectViewProps) {
  const versionsQuery = useContentVersions(project.contentItemId);

  // Bump this counter to force-remount the preview iframe (after a successful
  // draft push, so the right pane reflects the freshly-pushed content).
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const lastPushedSanityId = pushResult?.status === 'pushed' ? pushResult.sanityDocumentId : null;
  useEffect(() => {
    if (lastPushedSanityId) {
      setPreviewRefreshKey((k) => k + 1);
    }
  }, [lastPushedSanityId]);

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-body-md font-medium text-text-primary">{project.title}</div>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge variant={statusBadgeVariant(project.itemStatus)}>{project.itemStatus}</Badge>
              {project.reviewMeta && (
                <Badge
                  variant={
                    project.reviewMeta.reviewers.every((r) => r.status === 'approved') ? 'success' : 'warning'
                  }
                >
                  {project.reviewMeta.reviewers.filter((r) => r.status === 'approved').length}/
                  {project.reviewMeta.reviewers.length} approved
                </Badge>
              )}
              {project.reusedProject && <Badge variant="info">Reused project</Badge>}
            </div>
            <div className="text-body-xs text-text-tertiary mt-2">
              <span className="font-mono">{project.documentType}</span>
              {project.slug && <> · <span className="font-mono">{project.slug}</span></>}
              {' · '}<span>{project.language}</span>
            </div>
            <div className="text-body-xs text-text-tertiary mt-1">
              Sanity ID <code>{project.sanityDocumentId}</code>
              {' · '}
              <a href={project.url} target="_blank" rel="noreferrer" className="underline">{project.url}</a>
            </div>
            <div className="text-body-xs text-text-secondary mt-2">
              SEO keyword: {project.seoKeyword
                ? <code>{project.seoKeyword}</code>
                : <span className="text-text-tertiary">none configured</span>}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button onClick={() => onPush(false)} disabled={pushing}>
              {pushing ? 'Pushing…' : 'Push as draft'}
            </Button>
          </div>
        </div>
      </Card>

      <ReviewPanel project={project} />

      {project.keywordWarnings.length > 0 && (
        <Alert variant="warning" title="SEO keyword check">
          <ul className="list-disc pl-5">
            {project.keywordWarnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </Alert>
      )}

      {pushError instanceof Error && (
        <Alert variant="error">{pushError.message}</Alert>
      )}

      {pushResult?.status === 'draft_exists' && (
        <Alert variant="warning" title="A draft already exists in Sanity Studio">
          <p>{pushResult.message}</p>
          <div className="mt-2">
            <Button size="sm" onClick={() => onPush(true)} disabled={pushing}>
              Force overwrite the existing draft
            </Button>
          </div>
        </Alert>
      )}

      {pushResult?.status === 'pushed' && (
        <Alert variant="success" title="Draft pushed to Sanity">
          <p>
            Sanity document ID: <code>{pushResult.sanityDocumentId}</code>
            {pushResult.overwroteDraft && ' (overwrote existing draft)'}
          </p>
          <p className="text-body-xs text-text-tertiary mt-1">
            Studio path: <code>{pushResult.studioPath}</code>
          </p>
        </Alert>
      )}

      <div className={project.previewIframeUrl ? 'grid grid-cols-1 xl:grid-cols-2 gap-4' : ''}>
        <div className="space-y-4 min-w-0">
          <DiffView
            project={project}
            onSave={onSave}
            saving={saving}
            isBodyBased={project.isBodyBased}
          />
        </div>
        {project.previewIframeUrl && (
          <div className="space-y-4 min-w-0 xl:sticky xl:top-4 xl:self-start xl:max-h-[calc(100vh-2rem)]">
            <PreviewPanel
              iframeUrl={project.previewIframeUrl}
              pageUrl={project.url}
              refreshKey={previewRefreshKey}
              onRefresh={() => setPreviewRefreshKey((k) => k + 1)}
            />
          </div>
        )}
      </div>

      <ModificationsTimeline contentItemId={project.contentItemId} />

      <Card className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-body-md font-medium text-text-primary">Project history</h3>
          <span className="text-body-xs text-text-tertiary">{versionsQuery.data?.versions.length ?? 0} versions</span>
        </div>
        {versionsQuery.isLoading && <Spinner />}
        {versionsQuery.data?.versions.length === 0 && (
          <p className="text-body-xs text-text-tertiary">
            No edit history yet — every save will appear here.
          </p>
        )}
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {versionsQuery.data?.versions.map((v) => (
            <div
              key={v.id}
              className="text-body-xs text-text-secondary border border-border rounded px-2 py-1"
            >
              <div>
                v{v.versionNum} · {formatDayMonthYearTime(v.createdAt)} · <span className="font-mono">{v.editSource}</span>
              </div>
              {v.diffSummary && (
                <div className="italic text-text-tertiary mt-0.5">{v.diffSummary}</div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Modifications timeline ────────────────────────────────────────────────

const MODIFICATION_SOURCE_LABELS: Record<ModificationListItem['source'], string> = {
  rewrite_push: 'Pushed (your edits to AI proposal)',
  studio_poll: 'Studio edit (detected by daily poll)',
  manual: 'Manual',
};

const MODIFICATION_SOURCE_VARIANTS: Record<
  ModificationListItem['source'],
  'info' | 'success' | 'warning'
> = {
  rewrite_push: 'info',
  studio_poll: 'success',
  manual: 'warning',
};

function ModificationsTimeline({ contentItemId }: { contentItemId: string }) {
  const modsQuery = useRewriteModifications(contentItemId);
  const pollMutation = usePollRewriteNow();

  const mods = modsQuery.data?.modifications ?? [];

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-body-md font-medium text-text-primary">Modifications &amp; auto-learning</h3>
          <p className="text-body-xs text-text-tertiary mt-1">
            Captures the user edits applied at push time and the Studio edits detected by the daily Sanity poll.
            When a change reflects a generalisable rule, it is auto-applied to future rewrites.
          </p>
        </div>
        <div className="shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => pollMutation.mutate(contentItemId)}
            disabled={pollMutation.isPending}
          >
            {pollMutation.isPending ? 'Polling…' : 'Poll Sanity now'}
          </Button>
        </div>
      </div>

      {pollMutation.data && (
        <Alert variant={pollMutation.data.changed ? 'success' : 'info'}>
          {pollMutation.data.changed
            ? `Detected a change. ${pollMutation.data.ruleCreated ? 'A new rule was auto-applied.' : 'No new generalisable rule.'}`
            : `No changes detected${pollMutation.data.skippedReason ? ` (${pollMutation.data.skippedReason})` : ''}.`}
        </Alert>
      )}
      {pollMutation.error instanceof Error && (
        <Alert variant="error">{pollMutation.error.message}</Alert>
      )}

      {modsQuery.isLoading && <Spinner />}
      {!modsQuery.isLoading && mods.length === 0 && (
        <p className="text-body-xs text-text-tertiary">
          No modifications yet. Push a rewrite (or wait for the next daily poll) for entries to appear here.
        </p>
      )}

      <div className="space-y-2">
        {mods.map((m) => (
          <ModificationCard key={m.id} mod={m} contentItemId={contentItemId} />
        ))}
      </div>
    </Card>
  );
}

function ModificationCard({
  mod,
  contentItemId,
}: {
  mod: ModificationListItem;
  contentItemId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const reExtractMutation = useReExtractModification();

  const sourceLabel = MODIFICATION_SOURCE_LABELS[mod.source];
  const sourceVariant = MODIFICATION_SOURCE_VARIANTS[mod.source];

  return (
    <div className="border border-border rounded p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={sourceVariant}>{sourceLabel}</Badge>
            <span className="text-body-xs text-text-tertiary">
              {formatDayMonthYearTime(mod.detectedAt)}
            </span>
            {mod.sectionsChanged.length > 0 && (
              <span className="text-body-xs text-text-secondary">
                {mod.sectionsChanged.length} section{mod.sectionsChanged.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {mod.diffSummary && (
            <p className="text-body-sm text-text-primary">{mod.diffSummary}</p>
          )}
          {mod.sectionsChanged.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {mod.sectionsChanged.map((s) => (
                <code
                  key={s}
                  className="text-body-xs px-1.5 py-0.5 rounded bg-surface-secondary text-text-secondary"
                >
                  {s}
                </code>
              ))}
            </div>
          )}
        </div>
        <div className="shrink-0 flex flex-col gap-1 items-end">
          <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Hide diff' : 'Show diff'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => reExtractMutation.mutate({ modificationId: mod.id, contentItemId })}
            disabled={reExtractMutation.isPending}
          >
            {reExtractMutation.isPending ? 'Re-extracting…' : 'Re-extract'}
          </Button>
        </div>
      </div>

      {mod.rule ? (
        <div className="rounded bg-surface-secondary border border-border-light p-2">
          <div className="flex items-center gap-2">
            <Badge variant={mod.rule.isActive ? 'success' : 'warning'}>
              {mod.rule.isActive ? 'Active rule' : `Rule (${mod.rule.status})`}
            </Badge>
            <span className="text-body-xs text-text-tertiary">{mod.rule.category}</span>
          </div>
          <p className="text-body-sm text-text-primary mt-1">{mod.rule.rule}</p>
        </div>
      ) : (
        <p className="text-body-xs text-text-tertiary italic">
          {mod.extractionStatus === 'failed'
            ? `Extraction failed: ${mod.extractionError ?? 'unknown error'}.`
            : mod.extractionStatus === 'updated_existing'
            ? 'This change refined an existing reusable rule.'
            : mod.extractionStatus === 'duplicate'
            ? 'This change matched an existing reusable rule, so no duplicate was created.'
            : mod.extractionStatus === 'no_rule'
            ? 'No generalisable rule extracted from this change.'
            : 'Extraction pending…'}
        </p>
      )}

      {expanded && (
        <ModificationDiffView before={mod.beforeEnvelope} after={mod.afterEnvelope} />
      )}
    </div>
  );
}

function ModificationDiffView({
  before,
  after,
}: {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}) {
  const beforeText = useMemo(() => formatEnvelopeForDiff(before), [before]);
  const afterText = useMemo(() => formatEnvelopeForDiff(after), [after]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
      <div>
        <div className="text-body-xs text-text-tertiary mb-1">Before</div>
        <pre className="text-body-xs whitespace-pre-wrap break-words bg-surface-secondary p-2 rounded max-h-80 overflow-y-auto">
          {beforeText}
        </pre>
      </div>
      <div>
        <div className="text-body-xs text-text-tertiary mb-1">After</div>
        <pre className="text-body-xs whitespace-pre-wrap break-words bg-surface-secondary p-2 rounded max-h-80 overflow-y-auto">
          {afterText}
        </pre>
      </div>
    </div>
  );
}

function formatEnvelopeForDiff(env: Record<string, unknown>): string {
  // Body-based docs: prefer the human-readable body so the diff is reviewable.
  if (typeof env.body === 'string') {
    const title = typeof env.title === 'string' ? env.title : '';
    return [title, env.body].filter(Boolean).join('\n\n');
  }
  return JSON.stringify(env, null, 2);
}

interface DiffViewProps {
  project: ProjectState;
  onSave: (envelope: Record<string, unknown>, source: EditorSource) => void | Promise<void>;
  saving: boolean;
  isBodyBased: boolean;
}

type DiffMode = 'sections' | 'replace';

function DiffView({ project, onSave, saving, isBodyBased }: DiffViewProps) {
  const { existing, proposed, diff } = project;
  const [mode, setMode] = useState<DiffMode>('sections');

  const sortedSections = useMemo(() => {
    const order = { changed: 0, added: 1, removed: 2, unchanged: 3 } as const;
    return [...diff.perSection].sort((a, b) => order[a.kind] - order[b.kind]);
  }, [diff]);

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-body-md font-medium text-text-primary">Diff &amp; edit</h3>
          {diff.summary && (
            <p className="text-body-sm text-text-secondary mt-1">{diff.summary}</p>
          )}
          <p className="text-body-xs text-text-tertiary mt-1">
            Edit the proposed side directly. Each save creates a new version row and feeds the engine&apos;s learning loop.
          </p>
        </div>
        <div className="flex flex-col gap-2 items-end shrink-0">
          <div className="flex gap-1">
            <Badge variant="info">{diff.sectionsChanged.length} changed</Badge>
            <Badge variant="success">{diff.sectionsAdded.length} added</Badge>
            <Badge variant="error">{diff.sectionsRemoved.length} removed</Badge>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant={mode === 'sections' ? 'primary' : 'outline'} onClick={() => setMode('sections')}>
              By section
            </Button>
            <Button size="sm" variant={mode === 'replace' ? 'primary' : 'outline'} onClick={() => setMode('replace')}>
              Paste replacement
            </Button>
          </div>
        </div>
      </div>

      {mode === 'sections' ? (
        <div className="space-y-2">
          {sortedSections.map((section) => (
            <EditableSectionRow
              key={section.sectionKey}
              section={section}
              existingValue={existing[section.sectionKey]}
              proposedValue={proposed[section.sectionKey]}
              envelope={proposed}
              isBodyBased={isBodyBased}
              saving={saving}
              onSave={onSave}
            />
          ))}
        </div>
      ) : (
        <ReplaceMode envelope={proposed} isBodyBased={isBodyBased} saving={saving} onSave={onSave} />
      )}
    </Card>
  );
}

interface EditableSectionRowProps {
  section: ProjectState['diff']['perSection'][number];
  existingValue: unknown;
  proposedValue: unknown;
  envelope: Record<string, unknown>;
  isBodyBased: boolean;
  saving: boolean;
  onSave: (envelope: Record<string, unknown>, source: EditorSource) => void | Promise<void>;
}

function EditableSectionRow({
  section,
  existingValue,
  proposedValue,
  envelope,
  isBodyBased,
  saving,
  onSave,
}: EditableSectionRowProps) {
  const isBody = isBodyBased && section.sectionKey === 'body';
  // For removed-kind sections, proposed is undefined — seed the textarea with
  // the existing value so the user can choose to re-add it if they want.
  const seedValue = proposedValue === undefined ? existingValue : proposedValue;
  const initialText = useMemo(() => {
    if (seedValue === undefined) return '';
    return isBody && typeof seedValue === 'string'
      ? seedValue
      : JSON.stringify(seedValue, null, 2);
  }, [seedValue, isBody]);

  const [text, setText] = useState(initialText);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync local state when the proposed value is replaced from outside
  // (e.g. after a successful save or a re-run rewrite).
  useEffect(() => {
    setText(initialText);
    setDirty(false);
    setError(null);
  }, [initialText]);

  async function handleSave() {
    setError(null);
    let parsed: unknown;
    if (isBody) {
      parsed = text;
    } else {
      try {
        parsed = JSON.parse(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Invalid JSON');
        return;
      }
    }
    try {
      await onSave({ ...envelope, [section.sectionKey]: parsed }, 'section_edit');
      setDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  function handleReset() {
    setText(initialText);
    setDirty(false);
    setError(null);
  }

  return (
    <details
      open={section.kind !== 'unchanged'}
      className="border border-border rounded-md bg-surface-secondary"
    >
      <summary className="px-3 py-2 cursor-pointer text-body-sm text-text-primary hover:bg-surface-subtle flex items-center gap-2">
        <span className="font-medium">{section.sectionKey}</span>
        <Badge variant={badgeVariantFor(section.kind)}>{section.kind}</Badge>
        {dirty && <span className="ml-1 text-body-xs text-text-tertiary">(unsaved)</span>}
      </summary>
      <div className="border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
          <div className="p-3 space-y-1">
            <div className="text-body-xs font-medium text-text-tertiary">Current (Sanity)</div>
            <pre className="text-body-xs whitespace-pre-wrap break-words text-text-secondary max-h-72 overflow-y-auto">
              {existingValue === undefined ? '(missing)' : (typeof existingValue === 'string' ? existingValue : JSON.stringify(existingValue, null, 2))}
            </pre>
          </div>
          <div className="p-3 space-y-2">
            <div className="text-body-xs font-medium text-text-tertiary">
              Proposed (rewrite) — editable
              {!isBody && <span className="ml-2 text-text-tertiary">JSON</span>}
            </div>
            <textarea
              value={text}
              onChange={(e) => { setText(e.target.value); setDirty(true); }}
              rows={isBody ? 16 : 12}
              spellCheck={isBody}
              className="w-full font-mono text-body-xs rounded-md border border-border bg-surface-primary px-3 py-2 text-text-primary"
            />
            {error && <Alert variant="error">{error}</Alert>}
            {dirty && (
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save section'}
                </Button>
                <Button size="sm" variant="outline" onClick={handleReset}>
                  Reset
                </Button>
              </div>
            )}
          </div>
        </div>
        {section.fields.length > 0 && (
          <div className="border-t border-border p-3 space-y-1">
            <div className="text-body-xs font-medium text-text-secondary">Field-level changes</div>
            <ul className="text-body-xs text-text-tertiary space-y-1">
              {section.fields.slice(0, 30).map((f, i) => (
                <li key={`${f.path}-${i}`} className="font-mono">
                  <span className="font-bold">{f.kind}</span> {f.path}
                  {f.kind === 'changed' && (
                    <>
                      <div>before: {truncate(f.before)}</div>
                      <div>after: {truncate(f.after)}</div>
                    </>
                  )}
                </li>
              ))}
              {section.fields.length > 30 && (
                <li className="italic">…and {section.fields.length - 30} more</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}

interface ReplaceModeProps {
  envelope: Record<string, unknown>;
  isBodyBased: boolean;
  saving: boolean;
  onSave: (envelope: Record<string, unknown>, source: EditorSource) => void | Promise<void>;
}

function ReplaceMode({ envelope, isBodyBased, saving, onSave }: ReplaceModeProps) {
  const initial = useMemo(() => {
    return isBodyBased && typeof envelope.body === 'string'
      ? (envelope.body as string)
      : JSON.stringify(envelope, null, 2);
  }, [envelope, isBodyBased]);

  const [text, setText] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setText(initial);
    setError(null);
  }, [initial]);

  async function handleSave() {
    setError(null);
    if (isBodyBased) {
      try {
        await onSave({ ...envelope, body: text }, 'paste_replacement');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Save failed');
      }
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('Replacement must be a JSON object.');
      return;
    }
    try {
      await onSave(parsed as Record<string, unknown>, 'paste_replacement');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-body-xs text-text-tertiary">
        Paste a corrected version of the {isBodyBased ? 'body markdown' : 'full envelope JSON'}. Replaces the entire proposed content in one save.
        {!isBodyBased && (
          <span className="ml-2 inline-flex"><Badge variant="info">JSON object expected</Badge></span>
        )}
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={24}
        className="w-full font-mono text-body-xs rounded-md border border-border bg-surface-primary px-3 py-2 text-text-primary"
      />
      {error && <Alert variant="error">{error}</Alert>}
      <Button onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Replace proposed content'}
      </Button>
    </div>
  );
}

function badgeVariantFor(kind: 'added' | 'removed' | 'changed' | 'unchanged'): 'success' | 'error' | 'info' | 'default' {
  if (kind === 'added') return 'success';
  if (kind === 'removed') return 'error';
  if (kind === 'changed') return 'info';
  return 'default';
}

function statusBadgeVariant(status: ItemStatus): 'success' | 'warning' | 'default' {
  if (status === 'approved' || status === 'published') return 'success';
  if (status === 'review') return 'warning';
  return 'default';
}

function truncate(value: unknown, max = 200): string {
  if (value === undefined || value === null) return String(value);
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  return s.length > max ? s.slice(0, max) + '…' : s;
}

interface ReviewPanelProps {
  project: ProjectState;
}

function ReviewPanel({ project }: ReviewPanelProps) {
  const { user } = useAuth();
  const patchItem = usePatchContentItem();
  const approveItem = useApproveContentItem();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [slackError, setSlackError] = useState<string | null>(null);

  const reviewMeta = project.reviewMeta;
  const isCurrentUserPendingReviewer =
    !!reviewMeta?.reviewers.find(
      (r) => (r.email === user?.email || r.userId === user?.id) && r.status === 'pending',
    );
  const canApprove = project.itemStatus === 'review' && isCurrentUserPendingReviewer;

  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSendToReview() {
    if (selectedIds.size === 0) return;
    setSlackError(null);
    const reviewers = REVIEWERS
      .filter((r) => selectedIds.has(r.id))
      .map((r) => ({ userId: r.id, name: r.name, email: r.email }));
    const result = await patchItem.mutateAsync({
      id: project.contentItemId,
      updates: {
        status: 'review',
        reviewers,
        ...(note.trim() ? { reviewNote: note.trim() } : {}),
      },
    });
    const notify = (result as unknown as Record<string, unknown>).slackNotify as
      | { sent: string[]; failed: { email: string; reason: string }[]; error?: string }
      | undefined;
    if (notify?.error) {
      setSlackError(notify.error);
    } else if (notify?.failed?.length) {
      setSlackError(notify.failed.map((f) => `${f.email}: ${f.reason}`).join('\n'));
    }
    setPickerOpen(false);
    setSelectedIds(new Set());
    setNote('');
  }

  async function handleApprove() {
    await approveItem.mutateAsync(project.contentItemId);
  }

  if (project.itemStatus === 'draft') {
    return (
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-body-md font-medium text-text-primary">Review</h3>
            <p className="text-body-xs text-text-tertiary mt-1">
              Send this rewrite to teammates for review. Reviewers see the project in the Library and can open it here to read the diff and approve. The Sanity draft is what they actually compare against, so push first if you haven&apos;t.
            </p>
          </div>
          {!pickerOpen && (
            <Button size="sm" onClick={() => setPickerOpen(true)}>
              Send for review
            </Button>
          )}
        </div>
        {pickerOpen && (
          <div className="space-y-3 border-t border-border pt-3">
            <p className="text-body-xs font-medium text-text-primary">Select reviewers</p>
            <div className="space-y-1 border border-border rounded-md p-2 max-h-40 overflow-y-auto">
              {REVIEWERS.map((r) => (
                <label
                  key={r.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-secondary cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggle(r.id)}
                    className="h-4 w-4 cursor-pointer rounded-sm border-border accent-text-primary"
                  />
                  <span className="text-body-sm text-text-primary">{r.name}</span>
                  <span className="text-body-xs text-text-tertiary">{r.email}</span>
                </label>
              ))}
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Optional note for reviewers (Slack DM)"
              className="w-full rounded-md border border-border bg-surface-primary px-3 py-2 text-body-xs text-text-primary"
            />
            {slackError && <Alert variant="error">{slackError}</Alert>}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSendToReview}
                disabled={selectedIds.size === 0 || patchItem.isPending}
              >
                {patchItem.isPending ? 'Sending…' : `Notify ${selectedIds.size} on Slack`}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setPickerOpen(false);
                  setSelectedIds(new Set());
                  setNote('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  }

  if (!reviewMeta) {
    // Status is review/approved/published but no review block — admin override path.
    return null;
  }

  const approvedCount = reviewMeta.reviewers.filter((r) => r.status === 'approved').length;
  const total = reviewMeta.reviewers.length;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-body-md font-medium text-text-primary">Review progress</h3>
        <Badge variant={approvedCount === total ? 'success' : 'warning'}>
          {approvedCount}/{total} approved
        </Badge>
      </div>
      <div className="space-y-1">
        {reviewMeta.reviewers.map((r) => (
          <div key={r.userId} className="flex items-center justify-between text-body-xs">
            <span className="text-text-secondary">{r.name}</span>
            <Badge variant={r.status === 'approved' ? 'success' : 'default'}>
              {r.status === 'approved'
                ? `Approved ${r.approvedAt ? formatDayMonthYear(r.approvedAt) : ''}`
                : 'Pending'}
            </Badge>
          </div>
        ))}
      </div>
      {reviewMeta.note && (
        <p className="text-body-xs text-text-secondary border-t border-border pt-2">
          Note: <span className="italic">{reviewMeta.note}</span>
        </p>
      )}
      {canApprove && (
        <div className="border-t border-border pt-2">
          <Button size="sm" onClick={handleApprove} disabled={approveItem.isPending}>
            {approveItem.isPending ? 'Approving…' : 'Approve this rewrite'}
          </Button>
        </div>
      )}
    </Card>
  );
}

interface PreviewPanelProps {
  iframeUrl: string;
  pageUrl: string;
  refreshKey: number;
  onRefresh: () => void;
}

/**
 * Renders the staging website inside an iframe, in draft mode, so the user
 * can see the page they're rewriting next to the JSON diff. The iframe is
 * remounted via the `refreshKey` prop to pick up changes after each push.
 *
 * Note: the website's `Content-Security-Policy: frame-ancestors` must include
 * the OS origin (localhost:5173 / os.sequesto.tech) for the iframe to load.
 */
function PreviewPanel({ iframeUrl, pageUrl, refreshKey, onRefresh }: PreviewPanelProps) {
  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-body-md font-medium text-text-primary">Live preview (draft mode)</h3>
          <p className="text-body-xs text-text-tertiary">
            The website served from the staging host with the latest pushed Sanity draft. Refresh after pushing.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onRefresh}>
            Refresh
          </Button>
          <a
            href={pageUrl}
            target="_blank"
            rel="noreferrer"
            className="text-body-xs underline text-text-secondary self-center"
          >
            Open in new tab
          </a>
        </div>
      </div>
      <div className="border border-border rounded-md overflow-hidden bg-surface-subtle">
        <iframe
          key={refreshKey}
          src={iframeUrl}
          title="Page preview"
          className="w-full block"
          style={{ height: 'min(80vh, 1000px)' }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    </Card>
  );
}
